import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    AbstractControl, FormControl,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {MomentAdapter} from "../../../../../../shared/utils/moment.utils";
import {MovementCreating, MovementType} from "../../entity/accountingMovement.model";
import {AccountingAccountModel} from "../../../../account/api/entity/accountingAccount.model";
import {ValueAndLabelModel} from '../../../../../common/model/valueAndLabel.model';
import {
    AccountingAccountFindOneByAccountIdService
} from "../../../../account/api/service/AccountingAccountFindOneByAccountId.service";
import {BaseView} from "../../../../../core/view/base.view";
import {
    PaymentCardReaderSummaryFindAllService
} from "../../../../../sale/modules/payment/paymentCardReader/api/service/paymentCardReaderSummaryFindAll.service";
import {Subscription} from "rxjs";
import {
    PaymentCardReaderModel
} from "../../../../../sale/modules/payment/paymentCardReader/api/entity/paymentCardReader.model";
import {SelectElementV2} from "../../../../../../components/common/select/selectElementV2";
import {
    PaymentCardReaderFindManyPaginatedService
} from "../../../../../sale/modules/payment/paymentCardReader/api/service/paymentCardReaderFindManyPaginated.service";
import {Pageable} from "../../../../../../shared/model/core/pageable.model";
import {
    PaymentCardReaderStatus,
    PaymentCardReaderSummaryModel
} from "../../../../../sale/modules/payment/paymentCardReader/api/entity/paymentCardReaderSummary.model";

@Component({
    selector: 'app-accounting-movement-create-form',
    templateUrl: './accounting-movement-create-form.component.html',
    viewProviders: [
        AccountingAccountFindOneByAccountIdService,
        PaymentCardReaderSummaryFindAllService,
        PaymentCardReaderFindManyPaginatedService
    ]
})
export class AccountingMovementCreateFormComponent extends BaseView
    implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() formGroup: UntypedFormGroup;
    @Input() movementType: MovementType;
    @Input() accountId: number;
    @Input() amount: number;
    @Input() partnerId: number;
    @Input() label: string;
    @Input() paymentTypeValueToIgnore: string;
    @Input() enableCardReaderPayment = true;

    @Output() amountChanged: EventEmitter<number> = new EventEmitter<number>();
    @Output() errorEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() selectedPaymentTypeChangedEvent: EventEmitter<'CARD_READER' | 'OTHER'> = new EventEmitter<'CARD_READER' | 'OTHER'>();

    selectedSourceAccount: AccountingAccountModel;
    selectedTargetAccount: AccountingAccountModel;
    possibleTargetAccountType: string;

    subscriptions: Subscription[] = [];
    cardReadersElements: SelectElementV2<PaymentCardReaderSummaryModel>[] = [];
    selectedPaymentType: 'CARD_READER' | 'OTHER' = 'OTHER';
    noCardReader = true;

    constructor(private formBuilder: UntypedFormBuilder,
                public findByIdService: AccountingAccountFindOneByAccountIdService,
                public paymentCardReaderSummaryFindAllService: PaymentCardReaderSummaryFindAllService,
                public paymentCardReaderFindManyPaginatedService: PaymentCardReaderFindManyPaginatedService) {
        super();

        this.subscriptions.push(this.paymentCardReaderSummaryFindAllService.entities$.subscribe(value =>
        this.noCardReader = value?.length === 0));

        this.subscriptions.push(this.paymentCardReaderFindManyPaginatedService.page$.subscribe(page => {
            this.cardReadersElements = [];
            if (page) {
                this.cardReadersElements = page.content
                    .filter(value => value.status?.value === PaymentCardReaderStatus.PAYMENT_CARD_READER_STATUS_PAIRED)
                    .map(value => new SelectElementV2<PaymentCardReaderSummaryModel>(value.summary.id, value.summary.name, value.summary));
                if (this.cardReadersElements.length === 1) {
                    this.formGroup.patchValue({
                        cardReaderId: this.cardReadersElements[0].value
                    });
                }
            }
        }));
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(value => value.unsubscribe());
    }



    get sourceAccountIdFormControl(): AbstractControl {
        return this.formGroup.get('sourceAccountId');
    }

    get targetAccountIdFormControl(): AbstractControl {
        return this.formGroup.get('targetAccountId');
    }

    ngOnInit() {
        this.enrichFormGroup();
        this.movementTypeChanged();

    }

    ngAfterViewInit(): void {
        this.accountIdChanged();
        this.setAmount();
        this.setLabel();
        this.paymentCardReaderSummaryFindAllService.findAll();
    }

    changeSelectedPaymentType(paymentType: 'CARD_READER' | 'OTHER') {
        this.selectedPaymentType = paymentType;
        this.selectedPaymentTypeChanged();
    }

    selectedPaymentTypeChanged() {
        this.formGroup.removeControl('cardReaderId');
        this.formGroup.removeControl('date');
        if (this.selectedPaymentType === 'CARD_READER') {
            this.paymentCardReaderFindManyPaginatedService.findPaginated(Pageable.ofFirstPage(10))
            this.formGroup.addControl('cardReaderId', new FormControl(undefined, Validators.required));
        } else {
            this.formGroup.addControl('date', new UntypedFormControl(MomentAdapter.current().get(), Validators.required));

        }
        this.selectedPaymentTypeChangedEvent.emit(this.selectedPaymentType);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const formGroupChange = changes['formGroup'];
        const movementTypeChange = changes['movementType'];
        const accountIdChange = changes['accountId'];
        const amountChange = changes['amount'];

        if (formGroupChange && !formGroupChange.isFirstChange()) {
            this.enrichFormGroup();
        }
        if (movementTypeChange && !movementTypeChange.isFirstChange()) {
            this.movementTypeChanged();
        }

        if (accountIdChange && !accountIdChange.isFirstChange()) {
            this.accountIdChanged();
        }

        if (amountChange && !amountChange.isFirstChange()) {
            this.setAmount();
        }
        if (changes['label'] && !changes['label'].isFirstChange()) {
            this.setLabel();
        }
    }

    paymentTypeChanged(paymentType: ValueAndLabelModel) {
        this.formGroup.removeControl('check');
        if (paymentType?.value === 'CHECK') {
            this.formGroup.addControl('check',
                this.formBuilder.group({
                    number: this.formBuilder.control(undefined),
                    from: this.formBuilder.control(undefined),
                    bankValue: this.formBuilder.control(undefined)
                }));
        }
        this.refreshPossibleTargetAccountType();
    }

    refreshPossibleTargetAccountType() {
        const paymentTypeValue = this.formGroup.get('paymentTypeValue')?.value;
        if (paymentTypeValue === 'CHECK' || paymentTypeValue === 'CASH') {
            this.possibleTargetAccountType = 'CHECKOUT';
        }
        if (paymentTypeValue === 'CARD' || paymentTypeValue === 'BANK') {
            this.possibleTargetAccountType = 'BANK_ACCOUNT';
        }
    }

    public buildPayload(): MovementCreating {
        return {
            id: null,
            partnerId: this.partnerId,
            amount: this.formGroup.get('amount').value,
            comment: this.formGroup.get('comment').value,
            date: this.formGroup.get('date').value,
            labelValue: this.formGroup.get('labelValue').value,
            paymentTypeValue: this.formGroup.get('paymentTypeValue').value,
            sourceAccountId: this.sourceAccountIdFormControl?.value,
            targetAccountId: this.targetAccountIdFormControl?.value,
            check: this.formGroup.get('check')?.value,
            typeValue: MovementType[this.movementType]
        };
    }

    sourceAccountChanged(account: AccountingAccountModel) {
        this.selectedSourceAccount = account;
    }

    targetAccountChanged(account: AccountingAccountModel) {
        this.selectedTargetAccount = account;
    }

    movementTypeTransfer() {
        return this.movementType === MovementType.TRANSFER;
    }

    movementTypeRevenue() {
        return this.movementType === MovementType.REVENUE;
    }

    movementTypeExpenditure() {
        return this.movementType === MovementType.EXPENDITURE;
    }

    private accountIdChanged() {
        if (this.accountId) {
            this.findByIdService.loadEntity(this.accountId)
                .then(account => {
                    let control: AbstractControl;
                    if (this.movementTypeTransfer() || this.movementTypeExpenditure()) {
                        this.formGroup.patchValue({
                            sourceAccountId: this.accountId
                        });
                        control = this.sourceAccountIdFormControl;
                        this.selectedSourceAccount = account;
                    } else {
                        this.formGroup.patchValue({
                            targetAccountId: this.accountId
                        });
                        control = this.targetAccountIdFormControl;
                        this.selectedTargetAccount = account;
                    }


                    if (this.accountId) {
                        control?.disable();
                    } else {
                        control?.enable();
                    }
                })
                .catch((e) => {
                    this.toastControl.presentError(e);
                    this.errorEvent.emit();
                });
        }
    }

    private setAmount() {
        this.formGroup.patchValue({
            amount: this.amount
        });
    }

    private setLabel() {
        this.formGroup.patchValue({
            labelValue: this.label
        });
    }

    private movementTypeChanged() {
        if (this.movementTypeExpenditure()) {
            this.formGroup.addControl('sourceAccountId', new UntypedFormControl(undefined, Validators.required));
        } else if (this.movementTypeRevenue()) {
            this.formGroup.addControl('targetAccountId', new UntypedFormControl(undefined, Validators.required));
        } else {
            this.formGroup.addControl('sourceAccountId', new UntypedFormControl(undefined, Validators.required));
            this.formGroup.addControl('targetAccountId', new UntypedFormControl(undefined, Validators.required));
        }

    }

    private enrichFormGroup() {
        this.formGroup.removeControl('labelValue');
        this.formGroup.removeControl('amount');
        this.formGroup.removeControl('paymentTypeValue');
        this.formGroup.removeControl('comment');
        this.formGroup.removeControl('sourceAccountId');
        this.formGroup.removeControl('targetAccountId');


        this.formGroup.addControl('labelValue', new UntypedFormControl(undefined, Validators.required));
        this.formGroup.addControl('amount', new UntypedFormControl(undefined, Validators.required));
        this.formGroup.addControl('paymentTypeValue', new UntypedFormControl('CASH', Validators.required));
        this.formGroup.addControl('comment', new UntypedFormControl(undefined));

        this.selectedPaymentTypeChanged();

        this.formGroup.controls.amount.valueChanges.subscribe(value => {
            this.amountChanged.emit(value);
        });
        this.refreshPossibleTargetAccountType();
    }

}
